import { useTheme } from 'next-themes';
import { type ReactElement, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Toaster as Sonner, type ToasterProps, toast as sendToast } from 'sonner';
import { Bell, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { ToastLayout } from '~/components/ui/toast-layout';
import { cn } from '~/lib/utils';
import { t } from '@transifex/native';

type ToastVariant = 'notification' | 'warning' | 'error' | 'success';

export type ToastMessage = {
  title?: string;
  description: string;
  variant?: ToastVariant;
};

type ToastProps = ToastMessage & {
  id: string | number;
};

// Toast icon mapping
const toastIcons: Record<ToastVariant, ReactNode> = {
  notification: <Bell className="size-8 text-blue-500" />,
  warning: <AlertTriangle className="size-8 text-yellow-500" />,
  error: <AlertCircle className="size-8 text-red-500" />,
  success: <CheckCircle className="size-8 text-green-500" />,
};

// Toast border color mapping using design tokens
const toastBorderColors: Record<ToastVariant, string> = {
  notification: 'border-blue-500',
  warning: 'border-yellow-500',
  error: 'border-red-500',
  success: 'border-green-500',
};

// Responsive Toast component that matches Figma design
export function Toast({
  id,
  title = 'Notification',
  description,
  variant = 'notification',
}: ToastProps) {
  const icon = toastIcons[variant];
  const borderColor = toastBorderColors[variant];

  return (
    <ToastLayout title={title} icon={icon} borderColor={borderColor} toastId={id}>
      <p className="text-body-big leading-lg">{description}</p>
    </ToastLayout>
  );
}

export function Toaster({ ...props }: ToasterProps) {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
        } as CSSProperties
      }
      {...props}
    />
  );
}

export function showToast({ title, description, variant = 'notification' }: ToastMessage) {
  sendToast.custom(
    id => (
      <Toast
        id={id}
        title={title || t('Notification')}
        description={description}
        variant={variant}
      />
    ),
    { position: 'top-right', classNames: { toast: 'w-[500px]' } }
  );
}

/**
 * Show a toast with custom component as content.
 * Use this for complex toast designs.
 *
 * @param render - Render function receiving the toast `id` (needed for dismiss). Return any ReactNode.
 * @param options.size - Toast width preset (`default` = 520px, `wide` = 620px)
 */
export function showCustomToast(
  render: (id: string | number) => ReactElement,
  options?: { size?: 'default' | 'wide' }
) {
  const size = options?.size ?? 'default';
  const widthClass = size === 'wide' ? 'md:min-w-[620px]' : 'md:min-w-[520px]';

  sendToast.custom(render, {
    position: 'top-right',
    classNames: {
      toast: cn(
        `
          inset-x-md
          md:inset-x-auto md:w-auto
        `,
        widthClass
      ),
    },
  });
}

export function ShowToast({ toast }: { toast: ToastMessage & { ts: number } }) {
  const prevTs = useRef<number | null>(null);

  const { title, description, variant = 'notification', ts } = toast;
  useEffect(() => {
    if (prevTs.current === ts) {
      return;
    }
    prevTs.current = ts;

    showToast({ title, description, variant });
  }, [description, title, variant, ts]);
  return null;
}
